/**
 * useShellDomainControls - the panels' remaining controls and the shell's
 * effects: target reset and pinned cover, log clear and copy, map files,
 * reload, hand edits to a sheet, keyboard shortcuts, the roster onto the
 * wire, fire modes, macros and the forecast (odds, intent, firing line).
 *
 * A domain hook: it reads what it needs from the shell scope `s` and returns
 * what it declares (see DeathWatchSimulator). Hook order unchanged.
 * Extracted from DeathWatchSimulator.
 */
import useShellTargetActions from './useShellTargetActions';
import useShellMapFiles from './useShellMapFiles';
import useShellReload from './useShellReload';
import useShellSheetEdits from './useShellSheetEdits';
import useShellRosterPush from './useShellRosterPush';
import useShellFireModes from './useShellFireModes';
import useShellShortcuts from './useShellShortcuts';
import useShellMacros from './useShellMacros';
import useShellForecast from './useShellForecast';

/**
 * Target and log actions, map files, reload and sheet edits. Length: the
 * hooks' argument and return records (declarative).
 */
function useShellPanelVerbs(s) {
  const {
    selectedSlot,
    setRangeBand,
    actorId,
    actor,
    updateCombatantState,
    activeTab,
    setAtkProfiles,
    currentSlots,
    rawAttackToggles,
    setRawAttackToggles,
    showDialogAsync,
    logView,
    library,
    importMaps,
    pushToast,
    firestoreNarrative,
    setNarrativeClearTime,
    isLocked,
    isDefencePending,
    battlefieldDerivedValues,
    tabs,
    actorTarget,
    currentTab,
    pushHistory,
    capturePreActionSnapshot,
    historyTrack,
    updateTab,
    addNarrative,
    addRollNarrative,
    spendTurnRef,
  } = s;

  // ---- The target panel and the log: reset, pinned cover, clear, copy ----
  const { doResetTarget, coverPinned, unpinCover, applyBattlefieldMeasurement, doClearNarrative, doCopyNarrative } =
    useShellTargetActions({
      currentTab,
      activeTab,
      isLocked,
      isDefencePending,
      capturePreActionSnapshot,
      pushHistory,
      historyTrack,
      updateTab,
      updateCombatantState,
      addNarrative,
      battlefieldDerivedValues,
      setRangeBand,
      logView,
      setNarrativeClearTime,
      firestoreNarrative,
    });

  // Maps travel as files: export, import one file, merge a read folder.
  const { exportMaps, importMapEntries, importMapsFromFile } = useShellMapFiles({ library, importMaps, pushToast });

  // R and the footer's Reload button. See `hooks/useShellReload`.
  const handleReload = useShellReload({
    selectedSlot,
    currentSlots,
    actor,
    activeTab,
    rawAttackToggles,
    spendTurnRef,
    setAtkProfiles,
    updateCombatantState,
    addNarrative,
    addRollNarrative,
    setRawAttackToggles,
  });

  // Hand edits to a sheet from either panel - the same controls, pointed at
  // the target or at the actor (v7.1.0). The actor side reads THE SAME
  // PROJECTION THE ROLL USES, so the sheet and the to-hit agree.
  const {
    targetModHandlers,
    actorModHandlers,
    handleEditArmour,
    onActorToggleFrenzied: handleActorToggleFrenzied,
    onActorSetStandingReaction: handleActorSetStandingReaction,
    onActorTargetChange: handleActorTargetChange,
    onActorEditArmour: handleActorEditArmour,
    onActorReorderWeapons: handleActorReorderWeapons,
  } = useShellSheetEdits({ tabs, activeTab, actorId, actorTarget, updateTab, showDialogAsync });
  const handleAddDefenderMod = targetModHandlers.add;
  const handleRemoveDefenderMod = targetModHandlers.remove;
  const handleEditDefenderMod = targetModHandlers.edit;
  return {
    doResetTarget,
    coverPinned,
    unpinCover,
    applyBattlefieldMeasurement,
    doClearNarrative,
    doCopyNarrative,
    exportMaps,
    importMapEntries,
    importMapsFromFile,
    handleReload,
    actorModHandlers,
    handleEditArmour,
    handleActorToggleFrenzied,
    handleActorSetStandingReaction,
    handleActorTargetChange,
    handleActorEditArmour,
    handleActorReorderWeapons,
    handleAddDefenderMod,
    handleRemoveDefenderMod,
    handleEditDefenderMod,
  };
}

/**
 * Keyboard shortcuts, the roster push and fire modes. Length: the hooks'
 * argument records (declarative).
 */
function useShellKeysAndWire(s) {
  const {
    showHotkeys,
    editingSlot,
    tabCtx,
    demoScenario,
    selectedSlot,
    setSelectedSlot,
    setEditingSlot,
    fireMode,
    setFireMode,
    setTargetLocation,
    setSlotClipboard,
    sharedRoster,
    activeTab,
    setTabCtx,
    setPendingAttack,
    setShowHotkeys,
    isGm,
    sessionId,
    localCurrentProfile,
    currentSlots,
    perWeaponAttackState,
    setPerWeaponAttackState,
    armedWeaponKey,
    slot,
    isMelee,
    setAttackToggles,
    dialog,
    handleDialogClose,
    syncCombatantState,
    syncCombatantSheet,
    removeSharedCombatant,
    removeSharedToken,
    forgetHeard,
    rosterHydrated,
    permissions,
    clearPendingDefenceFirestore,
    currentTab,
    doUndo,
    turnTracker,
    toggleActionDrum,
    handleAttackOrChambered,
    handleNewTab,
    handleDuplicateTab,
    cycleRail,
    reactions,
    focusedReactionId,
    doResetTarget,
    doClearNarrative,
    handleReload,
  } = s;

  // ---- Keyboard shortcuts (side-effect hook) ----
  useShellShortcuts({
    cycleRail,
    turnTracker,
    handleAttackOrChambered,
    doResetTarget,
    handleReload,
    handleNewTab,
    handleDuplicateTab,
    toggleActionDrum,
    doClearNarrative,
    setShowHotkeys,
    setEditingSlot,
    setTabCtx,
    // One of the window's own overlays is up: Escape closes it and stops there (2026-10-15).
    overlayOpen: showHotkeys || editingSlot != null || tabCtx != null,
    setPendingAttack,
    setSlotClipboard,
    setSelectedSlot,
    doUndo,
    activeTab,
    currentSlots,
    dialog,
    handleDialogClose,
    clearPendingDefenceFirestore,
    focusedReactionId,
    reactions,
    sessionId,
  });

  // ---- Effects: the roster onto the wire, the countdown seed ----
  useShellRosterPush({
    // The shared slice: a hidden figure is never pushed (useHiddenFigures).
    roster: sharedRoster,
    sessionId,
    isGm,
    rosterHydrated,
    permissions,
    demoSeed: demoScenario?.houseRules?.roundCountdown,
    syncCombatantState,
    syncCombatantSheet,
    removeCombatant: removeSharedCombatant,
    removeToken: removeSharedToken,
    forgetHeard,
  });

  // ---- Fire modes, remembered per weapon, and the resets a new weapon brings ----
  const fireModes = useShellFireModes({
    slot,
    isMelee,
    selectedSlot,
    localCurrentProfile,
    fireMode,
    setFireMode,
    armedWeaponKey,
    perWeaponAttackState,
    setPerWeaponAttackState,
    setAttackToggles,
    currentTab,
    setTargetLocation,
    actorConds: s.actor?.state?.conditions,
  });
  return { fireModes };
}

/**
 * Macros and the forecast. Length: the hooks' argument records (declarative).
 */
function useShellOdds(s) {
  const {
    selectedSlot,
    setSelectedSlot,
    fireMode,
    setFireMode,
    hatred,
    setHatred,
    targetLocation,
    setTargetLocation,
    setRangeBand,
    actor,
    pendingAttack,
    attackMacros,
    setAttackMacros,
    frenzied,
    rerollDamage,
    setRerollDamage,
    localCurrentProfile,
    currentSlots,
    attackCustomMods,
    weaponOverrides,
    setAttackCustomMods,
    setWeaponOverrides,
    slot,
    isMelee,
    effectiveSlot,
    attackerCharStats,
    attackToggles,
    setAttackToggles,
    calledShotReduction,
    showDialogAsync,
    isLocked,
    unnaturalDosMode,
    applyConditionModifiers,
    condModOptions,
    liveMap,
    attackModFinal,
    actorProjection,
    rangeBand,
    currentTab,
    ledgerData,
    attackTriggerLabel,
    fireModes,
  } = s;

  // ---- Macro management ----
  const { currentMacros, handleRequestMacroName, handleSaveMacro, handleLoadMacro, handleDeleteMacro } = useShellMacros(
    {
      attackMacros,
      setAttackMacros,
      localCurrentProfile,
      showDialogAsync,
      currentSlots,
      selectedSlot,
      fireMode,
      attackToggles,
      attackCustomMods,
      weaponOverrides,
      hatred,
      rerollDamage,
      targetLocation,
      rangeBand,
      setSelectedSlot,
      setFireMode,
      setAttackToggles,
      setAttackCustomMods,
      setHatred,
      setRerollDamage,
      setTargetLocation,
      setRangeBand,
      setWeaponOverrides,
    },
  );

  // ---- The odds, the turn card's intent, the firing line and solutions ----
  const { probability, intent, firingLine, firingSolutions } = useShellForecast({
    slot,
    effectiveSlot,
    fireModes,
    fireMode,
    ledgerData,
    actor,
    actorProjection,
    currentTab,
    measurement: liveMap.measurement,
    attackTriggerLabel,
    isLocked,
    pendingAttack,
    unnaturalDosMode,
    applyConditionModifiers,
    condModOptions,
    attackModFinal,
    rangeBand,
    isMelee,
    hatred,
    frenzied,
    rerollDamage,
    targetLocation,
    attackerCharStats,
    attackToggles,
    calledShotReduction,
  });
  return {
    currentMacros,
    handleRequestMacroName,
    handleSaveMacro,
    handleLoadMacro,
    handleDeleteMacro,
    probability,
    intent,
    firingLine,
    firingSolutions,
  };
}

/**
 * @param {object} s - the shell scope (everything the rail domain and those
 *   before it declared).
 * @returns {object} the target / log / sheet verbs, map files, reload,
 *   `fireModes`, the macros and the forecast.
 */
export default function useShellDomainControls(s) {
  const panelVerbs = useShellPanelVerbs(s);
  const keysAndWire = useShellKeysAndWire({ ...s, ...panelVerbs });
  const odds = useShellOdds({ ...s, ...panelVerbs, ...keysAndWire });
  return { ...panelVerbs, ...keysAndWire, ...odds };
}
